import type {
  ProcessorCapabilityStatus,
  ProcessorCapabilityStatusMeta,
} from '@/types';

export type ProductionStatus =
  'LIVE' | 'PARTLY_LIVE' | 'NOT_LIVE_YET' | 'RETIRED';

/**
 * A processor's overall state in production, from the capability statuses the
 * server writes to its record on every deploy. Undefined when the record has
 * none: a processor with no transaction node, or one not redeployed since the
 * field was added.
 */
export const getProductionStatus = (
  statuses?: ProcessorCapabilityStatusMeta,
): ProductionStatus | undefined => {
  const values = new Set(
    Object.values(statuses ?? {}).flatMap((channels) =>
      Object.values(channels ?? {}).flatMap((types) =>
        Object.values(types ?? {}),
      ),
    ),
  );
  if (!values.size) {
    return undefined;
  }
  if (values.has('live')) {
    return values.size === 1 ? 'LIVE' : 'PARTLY_LIVE';
  }
  return values.has('dev') ? 'NOT_LIVE_YET' : 'RETIRED';
};

export const PRODUCTION_STATUS_DISPLAY: Record<
  ProductionStatus,
  {label: string; className: string}
> = {
  LIVE: {
    label: 'Live',
    className:
      'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  },
  PARTLY_LIVE: {
    label: 'Partly live',
    className:
      'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  },
  NOT_LIVE_YET: {
    label: 'Not live yet',
    className:
      'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  },
  RETIRED: {
    label: 'Retired',
    className: 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  },
};

export const CAPABILITY_STATUS_DISPLAY: Record<
  ProcessorCapabilityStatus,
  {label: string; className: string}
> = {
  live: PRODUCTION_STATUS_DISPLAY.LIVE,
  dev: {
    label: 'Not live in production',
    className: PRODUCTION_STATUS_DISPLAY.NOT_LIVE_YET.className,
  },
  retired: PRODUCTION_STATUS_DISPLAY.RETIRED,
};

/** Why the flags below are not the processor's status. */
export const ADMIN_FLAGS_NOTE =
  "Set by admins on this page. Only Deactivated has an effect: it hides the processor from the business portal's processor list. Routing ignores these flags; the Production status comes from the code.";
